import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type DateInputProps = {
  value?: Date;
  onChange?: (date?: Date) => void;
  className?: string;
  placeholder?: React.ReactNode;
  locale?: Locale;
  formatString?: string;
  localeCode?: string;
};

export function DateInput({
  value,
  onChange,
  className,
  placeholder,
  locale,
  formatString = 'PPP',
  localeCode,
}: DateInputProps) {
  const [internalDate, setInternalDate] = React.useState<Date>();
  const controlled = typeof value !== 'undefined' || typeof onChange !== 'undefined';
  const date = controlled ? value : internalDate;
  const [open, setOpen] = React.useState(false);

  const setDate = React.useCallback(
    (d?: Date) => {
      if (onChange) onChange(d);
      if (!controlled) setInternalDate(d);
    },
    [controlled, onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!date}
          className={`data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal ${className || ''}`}
        >
          <CalendarIcon />
          {date ? (
            localeCode ? (
              date.toLocaleDateString(localeCode, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })
            ) : (
              format(date, formatString, { locale })
            )
          ) : (
            <span>{placeholder || 'Sélectionner une date'}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={d => {
            setDate(d);
            if (d) setOpen(false);
          }}
          locale={locale}
          localeCode={localeCode}
        />
      </PopoverContent>
    </Popover>
  );
}
